import React, { useCallback, useContext, useState } from 'react';
import { TranslationContext } from '../../../contexts/TranslationContext';
import { Chain, ChainClient, SetChainDetailsRequest, TranslationAdminClient } from '../../../services/WebApiClient';
import { useClient } from '../../../util/useClient';
import { usePromise } from '../../EditorCommon/usePromise';
import { LoadSpinner } from '../../LoadSpinner';

export function SettingsAdmin(props: {
    chain: Chain,
    onChange: () => void,
}) {

    const client = useClient(ChainClient);
    const translationClient = useClient(TranslationAdminClient);
    const tc = useContext(TranslationContext);
    const [vatRate, setVatRate] = useState(props.chain.vatRate * 100.0);
    const [productRailTop, setProductRailTop] = useState(props.chain.productRailTop);
    const [productRailBottom, setProductRailBottom] = useState(props.chain.productRailBottom);
    const [productShipping, setProductShipping] = useState(props.chain.productShippingFee);
    const [languageId, setLanguageId] = useState(props.chain.languageId);
    const [isBusy, setIsBusy] = useState(false);

    async function edit(evt: React.FormEvent) {
        evt.preventDefault();
        setIsBusy(true);
        try {
            await client.setChainDetails(props.chain.id, new SetChainDetailsRequest({
                vatRate: vatRate / 100,
                productRailTopExternalId: productRailTop,
                productRailBottomExternalId: productRailBottom,
                productShippingFeeExternalId: productShipping,
                languageId: languageId,
            }));

        }
        finally {
            setIsBusy(false);
            props.onChange();
        }
    }

    const languages = usePromise(useCallback(async () => {
        try {
            const response = await translationClient.getLanguages();
            return response.languages;
        }
        catch (ex) {
            return undefined;
        }
    },
        [translationClient]
    ))

    if (isBusy) {
        return <LoadSpinner />
    }

    function setVatRateStr(vatStr: string) {
        try {
            const vr = parseFloat(vatStr);
            setVatRate(vr);
        }
        catch (ex) {
            console.warn("Could not convert vatrate", ex);
        }
    }

    return (
        <form onSubmit={edit}>
            <div className="form-group">
                <label htmlFor="chain-vat-rate">Vat Rate %</label>
                <input type="number"
                    step=".01"
                    id="chain-vat-rate"
                    className="form-control"
                    value={vatRate}
                    onChange={evt => setVatRateStr(evt.target.value)}
                />
            </div>

            <div className="form-group">
                <label htmlFor="chain-product-rail-top">Top Rail Product Number</label>
                <input type="string"
                    id="chain-product-rail-top"
                    className="form-control"
                    value={productRailTop}
                    onChange={evt => setProductRailTop(evt.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="chain-product-rail-bottom">Bottom Rail Product Number</label>
                <input type="string"
                    id="chain-product-rail-bottom"
                    className="form-control"
                    value={productRailBottom}
                    onChange={evt => setProductRailBottom(evt.target.value)}
                />
            </div>

            <div className="form-group">
                <label htmlFor="chain-product-shipping">Shipping Fee Product Number</label>
                <input type="string"
                    id="chain-product-shipping"
                    className="form-control"
                    value={productShipping}
                    onChange={evt => setProductShipping(evt.target.value)}
                />
            </div>

            {languages && (
                <div className="form-group">
                    <label htmlFor="chain-language">
                        {tc.translator("chain admin settings language", "Sprog")}
                    </label>
                    <select id="chain-language"
                        className="form-control"
                        value={languageId}
                        onChange={evt => setLanguageId(parseInt(evt.target.value))} >
                        {languages.map(lang => (
                            <option value={lang.id} key={lang.id}>
                                {lang.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <button className="btn btn-primary">
                Save
            </button>
        </form>
    );
}
